import React, { useState, useEffect } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, BadgeCheck, Eye, EyeOff } from "lucide-react";
import BlueButton from "./BlueButton";

export default function UserProfileCard({ user, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({ ...user });
  const [saving, setSaving] = useState(false);
  const [sessionRole, setSessionRole] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // ✅ Get session role from sessionStorage
  useEffect(() => {
    try {
      const authUserString = sessionStorage.getItem("authUserData");
      if (authUserString) {
        const authUser = JSON.parse(authUserString)?.data?.[0];
        if (authUser?.role) setSessionRole(Number(authUser.role));
      }
    } catch (err) {
      console.error("Error parsing session data:", err);
    }
  }, []);

  // ✅ Update form fields dynamically
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Handle save with API call
  const handleSave = async () => {
    if (sessionRole !== 2) {
      alert("You are not authorized to edit this profile.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        business_name: form.business_name,
        mobile: form.mobile,
        email: form.email,
        state: form.state,
        district: form.district,
        gst: form.gst,
        password: form.password,
        owner_name: form.owner_name,
        pan: form.pan,
        pincode: form.pincode,
      };

      const res = await axios.post(
        `https://goelectronix.in/api/v1/org_user/${user.id}`,
        payload
      );

      if (res.data.status === true) {
        alert("User updated successfully!");
        setIsEditing(false);
        onUpdate?.(form);
      } else {
        alert(res.data.message || "Failed to update user.");
      }
    } catch (err) {
      console.error("Save failed:", err);
      alert("Error saving data. Please try again later.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 shadow-md bg-white p-6 transition-all hover:shadow-lg">
      {/* ✅ Profile Header */}
      {!isEditing && (
      <div className="mb-6 flex items-center gap-4">
        <div className="grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow">
          <BadgeCheck className="h-8 w-8" />
        </div>
        <div>
          <div className="rounded-full bg-blue-50 px-3 py-0.5 text-xs font-semibold text-blue-700 shadow-sm">
            {user.org_code}
          </div>
          <h3 className="mt-2 text-lg font-semibold">{user.owner_name}</h3>
          <p className="text-xs text-slate-500">{user.business_name}</p>
        </div>
      </div>
      )}

      {/* ✅ Editable Mode */}
      {isEditing ? (
        sessionRole === 2 ? (
          <div className="grid grid-cols-2 gap-4">
            {[
                { label: "Owner Name", name: "owner_name" },
                { label: "Business Name", name: "business_name" },
                { label: "Email", name: "email", type: "email" },
                { label: "Mobile", name: "mobile", type: "tel" },
                { label: "State", name: "state" },
                { label: "District", name: "district" },
                { label: "Pincode", name: "pincode" },
                { label: "GST", name: "gst" },
                ].map((field) => (
                <div key={field.name}>
                    <label className="text-xs text-slate-500 block mb-1">
                    {field.label}
                    </label>
                    <input
                    type={field.type || "text"}
                    name={field.name}
                    value={form[field.name] || ""}
                    onChange={handleChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                    />
                </div>
                ))}

                {/* ✅ PAN and New Password side by side */}
                <div className="grid grid-cols-2 gap-4 col-span-2">
                <div>
                    <label className="text-xs text-slate-500 block mb-1">PAN</label>
                    <input
                    type="text"
                    name="pan"
                    value={form.pan || ""}
                    onChange={handleChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Enter PAN"
                    />
                </div>

                <div className="relative">
                    <label className="text-xs text-slate-500 block mb-1">New Password</label>
                    <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={form.password || ""}
                    onChange={handleChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Enter new password"
                    />
                    <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-8 text-slate-500 hover:text-blue-600"
                    >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                </div>
                </div>


            {/* ✅ Save / Cancel Buttons */}
            <div className="sm:col-span-2 flex gap-3 mt-4">
              <BlueButton
              
                variant="default"
                className="rounded-xl bg-blue-600 hover:bg-blue-700"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </BlueButton>
              <BlueButton
                
                variant="outline"
                className="rounded-xl"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </BlueButton>
            </div>
          </div>
        ) : (
          <p className="text-sm text-red-500">
            You do not have permission to edit this user.
          </p>
        )
      ) : (
        <>
          {/* ✅ Display Mode */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            {/* Contact Info */}
            <div className="rounded-xl border bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Contact
              </p>
              <div className="mt-2 space-y-2">
                <div className="flex items-start gap-1 text-sm text-blue-600 w-full break-all whitespace-normal">
                  <Mail className="h-3.5 w-3.5 mt-0.5 shrink-0 flex-none" />
                  <a
                    href={`mailto:${user.email}`}
                    className="block break-words whitespace-normal leading-snug w-full overflow-hidden text-ellipsis"
                    style={{ wordBreak: "break-word" }}
                  >
                    {user.email || "—"}
                  </a>
                </div>

                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4" /> {user.mobile}
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> {user.district}, {user.state}
                </div>
              </div>
            </div>

            {/* Identifiers */}
            <div className="rounded-xl border bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Identifiers
              </p>
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-xs text-slate-500">District</p>
                  <p className="font-medium">{user.district}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">State</p>
                  <p className="font-medium">{user.state}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Pincode</p>
                  <p className="font-medium">{user.pincode}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">PAN</p>
                  <p className="font-medium">{user.pan}</p>
                </div>
              </div>
            </div>
          </div>

          {/* ✅ Action Buttons */}
          <div className="mt-6 flex flex-wrap gap-3">
            {sessionRole === 2 && ( 
              <BlueButton
                variant="default"
                className="rounded-xl bg-blue-600 hover:bg-blue-700"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </BlueButton>
            )}
            {/* <BlueButton
              brand={BRAND}
              variant="outline"
              className="rounded-xl"
            >
              Export
            </BlueButton>
            <BlueButton
              brand={BRAND}
              variant="destructive"
              className="rounded-xl"
            >
              Delete
            </BlueButton> */}
          </div>
        </>
      )}
    </div>
  );
}
